import { FlightDetails, ConnectionFlight } from '@pages/Loads/types';
import axiosClient from './axios-client';
import { AxiosResponse } from 'axios';

const GET_FLIGHT_NUMBER_URL = '/Loads/GetFlightNumber';

const GET_ALL_FLIGHT_NUMBERS_URL = '/Loads/GetAllFlightNumbers';

const GET_LAST_FLIGHT_NUMBER_URL = '/Loads/GetLastFlightNumber';

const GET_DEPARTURE_CODES_URL = '/Loads/GetDepartureCodes';

const GET_ARRIVAL_CODES_URL = '/Loads/GetArrivalCodes';

const GET_LAST_ROUTE_URL = '/Loads/GetLastRoute';


interface FlightSearchParams {
  FlightNumber?: string;
  Source?: string;
  Destination?: string;
  Dates?: any;
  IsNearbyDeparture?: boolean;
  IsNearbyArrival?: boolean;
  Stops?: number;
  AirlineName?: string;
  LeaveTime?: string;
  ArrivalTime?: string;
  IncludeDirect?: boolean;
  IncludeConnections?: boolean;
  SortBy?: string;
  SortOrder?: string;
  PageNumber: number;
  PageSize: number;
}

export interface FlightSearchFacets {
  stops?: number[];
  directHasOvernightArrival?: boolean;
  directCount?: number;
  connectionCount?: number;
  [key: string]: any;
}

interface FlightSearchResponse {
  isSuccess: boolean;
  message: string;
  status: number;
  data: {
    items: FlightDetails[];
    pageNumber: number;
    pageSize: number;
    total: number;
    connectionTotal?: number;
    connections?: ConnectionFlight[];
    facets?: FlightSearchFacets;
  };
  facets?: FlightSearchFacets;
  count: number;
}

export interface FlightSuggestionDirectiveFilters {
  stops?: number;
  includeDirect?: boolean;
  includeConnections?: boolean;
  leaveTime?: string;
  arrivalTime?: string;
  duration?: string;
  airlineName?: string;
  [key: string]: any;
}

export interface FlightSuggestionDirective {
  sortBy?: string;
  sortDirection?: "asc" | "desc";
  filters?: FlightSuggestionDirectiveFilters;
}

export interface FlightSuggestion {
  id: string;
  label: string;
  appliesTo: "direct" | "connections" | "both";
  directive: FlightSuggestionDirective;
}

export interface FlightSuggestionsPayload {
  searchContext: any;
  facets: any;
  appliedSuggestionId: string | null;
  locale: string;
}

export interface FlightSuggestionsResponse {
  suggestions: FlightSuggestion[];
  generatedBy?: "llm" | "fallback" | string;
  cacheHit?: boolean;
}

export const fetchFlightSuggestions = async (
  payload: FlightSuggestionsPayload
): Promise<FlightSuggestionsResponse | null> => {
  try {
    const response: any = await axiosClient.post(
      "/Loads/FlightSuggestions",
      payload
    );
    const data = response?.data ?? response;
    if (data && Array.isArray(data.suggestions)) {
      return data as FlightSuggestionsResponse;
    }
    return null;
  } catch (error) {
    console.error("Silent log: Error fetching flight suggestions", error);
    return null;
  }
};



interface PostRequestItem {
  airlineId: number;
  flightNumber: string;
  sourceCode: string;
  destinationCode: string;
  departureDate: string;  
  arrvalDate: string;    
  depterm: string;
  arrterm: string;
  duration: string;
  elptim: string;
  depcity: string;
  arrcity: string;
}

interface PostFlightRequest {
  postRequest: PostRequestItem[];
}


/* eslint-disable @typescript-eslint/no-empty-interface */
interface PostFlightResponse {}
/* eslint-enable @typescript-eslint/no-empty-interface */

const fetchFlights = async (params: FlightSearchParams): Promise<FlightSearchResponse> => {
  try {
    const response = await axiosClient.get<FlightSearchResponse>('/Loads/FlightSearch', {
      params,
      paramsSerializer: (params) => {
        const searchParams = new URLSearchParams();
        
        Object.keys(params).forEach(key => {
          const value = params[key];
          
          if (Array.isArray(value)) {
            // For arrays, add each value with the same key name (without [])
            value.forEach(item => {
              searchParams.append(key, item);
            });
          } else if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
          }
        });
        
        return searchParams.toString();
      }
    });
    // Connection results are now paged server-side (PageNumber/PageSize), so we
    // no longer slice them to 6 on the client. Removing that cap is what lets
    // the "Connected" filter show all dates' results across pages.
    return response as unknown as FlightSearchResponse;
  } catch (error) {
    console.error('Error fetching flight data:', error);
    throw error;
  }
};

const postFlightRequest = async (requestData: PostFlightRequest): Promise<PostFlightResponse> => {
  try {
    
    const response = await axiosClient.post<PostFlightResponse>("/Loads/PostRequest", requestData);
    return response.data;
  } catch (error) {
    console.error("Error posting flight request:", error);
    throw error;
  }
};



export interface DepartureCode {
  departureCode: string;
}

export interface GetDepartureCodesParams {
  search?: string;
  arrivalCode?: string;
}


export interface ArrivalCode {
  arrivalCode: string; 
}

export interface GetArrivalCodesParams {
  search?: string;
  departureCode?: string; 
}

export  {fetchFlights, postFlightRequest};


interface FlightNumber {
  flightNumber: string;
  date: string;
}

interface FlightRoute {
  source: string;
  destination: string;
  date: string;
}

interface FlightHistoryResponse {
  recentSearches: string[];
  lastSearchFlightNumber: FlightNumber | null;
  lastSearchRoute: FlightRoute | null;
}

export const getFlightNumberHistory = async (): Promise<{
  recentSearches: string[];
  lastFlightNumbers: FlightNumber[];
  lastRoutes: FlightRoute[];
}> => {
  try {
    // Since the axiosClient already handles the baseURL and Authorization header
    // through interceptors, we just need to specify the endpoint
    const response = await axiosClient.get<FlightHistoryResponse>(
      '/Loads/GetFlightNumberHistory'
    )as unknown as FlightHistoryResponse;

    // The response.data is automatically returned by your interceptor
    const data = response; // No need for .data since interceptor handles it

    return {
      recentSearches: data.recentSearches || [],
      lastFlightNumbers: data.lastSearchFlightNumber ? [data.lastSearchFlightNumber] : [],
      lastRoutes: data.lastSearchRoute ? [data.lastSearchRoute] : [],
    };
  } catch (error) {
    console.error("Error fetching flight history:", error);
    return {
      recentSearches: [],
      lastFlightNumbers: [],
      lastRoutes: [],
    };
  }
};



export const getFlightNumbers = async (
  search: string
): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosClient.get(
      GET_FLIGHT_NUMBER_URL,
      {
        params: { search },
      }
    );

    return response;
  } catch (error) {
    console.error('Error fetching flight numbers:', error);
    throw error;
  }
};

// Fetches the complete flight number list. Same response envelope as
// GetFlightNumber (SearchHistoryResponseModel), but only flightNumber is
// populated on each item. Used once at load; filtering happens locally.
export const getAllFlightNumbers = async (): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosClient.get(
      GET_ALL_FLIGHT_NUMBERS_URL
    );

    return response;
  } catch (error) {
    console.error('Error fetching flight numbers:', error);
    throw error;
  }
};

export const getLastFlightNumber = async (): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosClient.get(
      GET_LAST_FLIGHT_NUMBER_URL
    );

    return response;
  } catch (error) {
    console.error('Error fetching last flight number:', error);
    throw error;
  }
};


export const getDepartureCodes = async (
  search: string
): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosClient.get(
      GET_DEPARTURE_CODES_URL,
      {
        params: { search },
      }
    );

    return response;
  } catch (error) {
    console.error('Error fetching departure codes:', error);
    throw error;
  }
};

// Fetches the complete airport list (the endpoint returns the full dataset
// regardless of the search term). Used once at load; filtering happens locally.
export const getAllAirportCodes = async (): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosClient.get(
      GET_DEPARTURE_CODES_URL,
      {
        params: { search: '' },
      }
    );

    return response;
  } catch (error) {
    console.error('Error fetching airport codes:', error);
    throw error;
  }
};

export const getArrivalCodes = async (
  search: string
): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosClient.get(
      GET_ARRIVAL_CODES_URL,
      {
        params: { search },
      }
    );

    return response;
  } catch (error) {
    console.error('Error fetching arrival codes:', error);
    throw error;
  }
};

export const getLastRoute = async (): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosClient.get(
      GET_LAST_ROUTE_URL
    );

    return response;
  } catch (error) {
    console.error('Error fetching last route:', error);
    throw error;
  }
};

// --- Recent Searches (flight_number / route) ---
const RECENT_SEARCH_LIST_URL = '/RecentSearch/List';
const RECENT_SEARCH_SAVE_URL = '/RecentSearch/Save';

export interface RecentSearchItem {
  id?: number;
  searchType?: string;
  flightNumber?: string | null;
  sourceCode?: string | null;
  destinationCode?: string | null;
  sourceName?: string | null;
  destinationName?: string | null;
  updatedDate: string;
}

export interface SaveRecentSearchPayload {
  flightNumber?: string;
  sourceCode?: string;
  destinationCode?: string;
}

export const getRecentSearches = async (
  limit = 10,
): Promise<RecentSearchItem[]> => {
  try {
    const response: any = await axiosClient.get(RECENT_SEARCH_LIST_URL, {
      params: { limit },
    });
    const data = response?.data ?? response;
    const items: RecentSearchItem[] = Array.isArray(data)
      ? data
      : data?.items || [];
    return items;
  } catch (error) {
    console.error('Error fetching recent searches:', error);
    return [];
  }
};

export const saveRecentSearch = async (
  payload: SaveRecentSearchPayload,
): Promise<void> => {
  try {
    await axiosClient.post(RECENT_SEARCH_SAVE_URL, payload);
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.message || error?.message || 'Error saving recent search';
    console.error('Error saving recent search:', errorMessage);
  }
};